import React, { useCallback, useMemo, useState } from "react";

import {
  MenuButton,
  IconButton,
  Text,
  DataTable,
  Popover,
  useDataTable,
  createColumnHelper,
  Menu,
  Button,
  ScrollArea,
} from "@puzzle/ui";
import { Box, Loader } from "@puzzle/ve";
import { Arrow, Ellipse } from "@puzzle/icons";
import { fontWeights, S, styled, vars } from "@puzzle/theme";
import { formatMoney, parseDate } from "@puzzle/utils";

import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
import links from "lib/links";

import { useCompanyDateFormatter } from "components/companies/useCompanyDateFormatter";
import { InfiniteScrollFooter } from "components/common/InfiniteScrollFooter";

import { PagedPayrollItemFragment } from "./graphql.generated";
import { TransparencyTable } from "./TransparencyTable";
import { SummaryTable } from "./SummaryTable";
import { RelatedTransactionsTable } from "./RelatedTransactionsTable";
import { usePayrollPage, calculateTotal } from "./PayrollProvider";
import { UpdateMode, UpsertPayrollDrawer } from "./UpsertPayrollDrawer";
import { NoDataMessage } from "./NoDataMessage";
import { MiniHeader, Block, BlockText } from "./styles";
import { DeletePayrollModal } from "./DeletePayrollModal";
import {
  EducationBanner,
  buildBannerDescription,
} from "components/common/EducationBanner/EducationBanner";

import { DATA_TEST_IDS } from "./constants";
import { CrossSellGusto } from "components/crossSell/CrossSellGusto";
import { AccountType, AccountWithIntegrationFragment } from "graphql/types";
import { useFinancialInstitutions } from "components/integrations/shared/FinancialInstitutionsProvider";

const SummaryHeader = styled(Text, {
  fontSize: "$headingS",
  marginBottom: "$2",
  display: "flex",
  color: "$text-neutral-base",
});

const SummaryTables = styled("div", {
  display: "flex",
  gap: "$4",

  [`${DataTable}`]: {
    border: "1px solid $outline-neutral-subdue",

    "tfoot tr": {
      backgroundColor: vars.colors["surface-neutral-highlight"],

      td: {
        color: "$text-neutral-base",
        fontWeight: 700,
      },
    },
  },
});

const NavigationButton = styled(IconButton, {
  borderColor: "$outline-neutral-base !important",

  "&:hover": {
    borderColor: "$outline-neutral-highlight !important",
  },

  "&:active": {
    borderColor: "$outline-neutral-base !important",
  },
});

const columnHelper = createColumnHelper<PagedPayrollItemFragment>();

export const shouldCrossSellGustoOnPayrollPage = (
  accounts: Pick<AccountWithIntegrationFragment, "type">[],
  accountsLoading: boolean
) => {
  if (isPosthogFeatureFlagEnabled(FeatureFlag.CrossSellGustoToAllCompanies)) {
    return true;
  }

  if (isPosthogFeatureFlagEnabled(FeatureFlag.CrossSellGustoToCompaniesWithoutPayroll)) {
    const hasPayrollAccount = accounts.some((account) => account.type === AccountType.Payroll);
    return !accountsLoading && !hasPayrollAccount;
  }

  return false;
};

export const Payroll = () => {
  const {
    pagedPayrolls,
    pagedPayrollsLoading,
    activePayrollLoading,
    activePayroll,
    setActivePayroll,
    popoverOpen,
    setPopoverOpen,
    fetchNextPage,
    hasMore,
    slug,
    activePayrollIndex,
  } = usePayrollPage();
  const showEducationBanner = isPosthogFeatureFlagEnabled(FeatureFlag.EducationBanners);
  const { accounts, accountsLoading } = useFinancialInstitutions();
  const formatter = useCompanyDateFormatter({
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

  const pagedPayrollFromSlug = useMemo(() => {
    return slug ? pagedPayrolls.find(({ id }) => id === slug) : pagedPayrolls[0];
  }, [pagedPayrolls, slug]);

  const isNavigationDisabled = useMemo(
    () => pagedPayrollsLoading || pagedPayrolls.length === 0,
    [pagedPayrollsLoading, pagedPayrolls]
  );

  const onNext = useCallback(() => {
    let index = activePayrollIndex + 1;
    if (index > pagedPayrolls.length - 1) {
      index = 0;
    }

    setActivePayroll(pagedPayrolls[index].id);
  }, [activePayrollIndex, pagedPayrolls, setActivePayroll]);

  const onPrevious = useCallback(() => {
    let index = activePayrollIndex - 1;
    if (index < 0) {
      index = pagedPayrolls.length - 1;
    }

    setActivePayroll(pagedPayrolls[index].id);
  }, [activePayrollIndex, pagedPayrolls, setActivePayroll]);

  const [updateMode, setUpdateMode] = useState<UpdateMode>();
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const renderBody = useCallback(() => {
    if (activePayroll) return <PayrollDetails />;

    if (!pagedPayrollsLoading && !activePayrollLoading && pagedPayrolls.length === 0) {
      return (
        <div>
          <NoDataMessage />
        </div>
      );
    }

    if (slug && !activePayrollLoading && !activePayroll) {
      return (
        <div>
          <Text variant="headingM" color="text-neutral-mostSubdue">
            Payroll not found
          </Text>
        </div>
      );
    }

    if (pagedPayrollsLoading || activePayrollLoading) {
      return (
        <Box css={{ height: 600 }}>
          <Loader size={32} />
        </Box>
      );
    }
  }, [activePayroll, activePayrollLoading, pagedPayrolls, pagedPayrollsLoading, slug]);

  const _payrollFallback = pagedPayrollFromSlug ?? activePayroll;

  if (shouldCrossSellGustoOnPayrollPage(accounts, accountsLoading)) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CrossSellGusto />
      </div>
    );
  }
  return (
    <Box css={{ maxWidth: 1240 }} data-testid={DATA_TEST_IDS.PAYROLL_SCREEN}>
      {showEducationBanner && (
        <EducationBanner
          feature="Payroll"
          title="Managing payroll with Puzzle"
          description={buildBannerDescription({
            heading: `Puzzle's payroll functionality makes it easy to keep your financial records accurate and up to date.
            Whether using an integrated payroll provider or manually entering payroll data,
            Puzzle ensures that payroll is correctly mapped into your general ledger (GL) accounts and reconciled with your bank transactions.
            With Puzzle, you can:`,
            items: [
              "Connect Gusto, Rippling, or Deel - Puzzle automatically pulls payroll data, including salaries, taxes, and benefits, then splits them into employee vs. employer portions and assigns them to the correct GL accounts.",
              "Match payroll transactions - Payroll payments, tax withholdings, and benefits deductions are matched against bank transactions, reducing the need for manual clean-up.",
              "Manually enter payroll - If you use a payroll provider that isn't integrated, Puzzle allows you to enter summarized payroll data, specifying salaries, net pay, taxes, and benefits for employees and contractors.",
            ],
            footer: (
              <Box css={{ display: "flex", flexDirection: "column", gap: S["1"] }}>
                <Text>
                  <Text css={{ fontWeight: fontWeights.bold }}>Note: </Text>Payroll can be complex,
                  and each business may have unique payroll structures. Puzzle automates most of the
                  process, but manual reconciliations may still be required to ensure accuracy.
                </Text>
                <Text>
                  If you use a payroll provider that isn't currently supported, let us know who you
                  use!
                </Text>
              </Box>
            ),
          })}
          learnMore={links.education.payroll.learnMore}
          showCloseButton
        />
      )}
      <UpsertPayrollDrawer
        onOpenChange={() => setUpdateMode(updateMode ? undefined : updateMode)}
        open={!!updateMode}
        activePayroll={activePayroll}
        updateMode={updateMode}
      />
      {activePayroll && (
        <DeletePayrollModal
          onOpenChange={() => setDeleteModalIsOpen(!deleteModalIsOpen)}
          open={deleteModalIsOpen}
          activePayroll={activePayroll}
        />
      )}
      <Box
        css={{
          display: "flex",
          paddingBottom: S["2"],
          alignItems: "center",
          gap: S["2"],
        }}
        data-testid={DATA_TEST_IDS.PAYROLL_SCREEN_CONFIGURATION}
      >
        <Popover
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          side="bottom"
          align="start"
          css={{
            background: "$surface-neutral-subdue",
            border: "1px solid $outline-neutral-subdue",
          }}
          trigger={
            <MenuButton
              variant="secondary"
              size="small"
              disabled={pagedPayrollsLoading || pagedPayrolls.length === 0}
            >
              {(pagedPayrollsLoading && pagedPayrolls.length === 0) ||
              (!pagedPayrollFromSlug && activePayrollLoading)
                ? "Loading..."
                : _payrollFallback
                  ? `Payroll posted on ${formatter.format(parseDate(_payrollFallback.checkDate))}`
                  : "Select a payroll"}
            </MenuButton>
          }
        >
          <ScrollArea css={{ height: 400 }}>
            <ScrollArea.Viewport css={{ padding: "$2" }}>
              <MiniPayrollsTable />
              {hasMore && (
                <Box css={{ display: "flex", marginTop: `-${S["5"]}`, justifyContent: "center" }}>
                  <InfiniteScrollFooter
                    loading={pagedPayrollsLoading}
                    fetchNextPage={fetchNextPage}
                  />
                </Box>
              )}
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar orientation="vertical">
              <ScrollArea.Thumb variant="shadowed" />
            </ScrollArea.Scrollbar>
          </ScrollArea>
        </Popover>
        <Box css={{ display: "flex", gap: S["1"] }}>
          <NavigationButton
            onClick={onNext}
            disabled={
              isNavigationDisabled ||
              activePayrollIndex === pagedPayrolls.length - 1 ||
              activePayrollIndex === -1
            }
            size="compact"
            circle
          >
            <Arrow fill="currentColor" rotate={-180} />
          </NavigationButton>
          <NavigationButton
            onClick={onPrevious}
            disabled={isNavigationDisabled || activePayrollIndex === 0}
            circle
            size="compact"
          >
            <Arrow fill="currentColor" />
          </NavigationButton>
        </Box>
        <Box css={{ display: "flex", gap: S["2"], marginLeft: "auto", alignItems: "center" }}>
          <Menu
            align="end"
            trigger={
              <NavigationButton circle size="compact" disabled={!activePayroll}>
                <Ellipse fill="currentColor" />
              </NavigationButton>
            }
          >
            {activePayroll && (
              <Menu.Group>
                <Menu.Item onClick={() => setUpdateMode("edit")}>Edit this payroll</Menu.Item>
                <Menu.Item onClick={() => setDeleteModalIsOpen(true)}>
                  Delete this payroll
                </Menu.Item>
              </Menu.Group>
            )}
          </Menu>
          <Button
            data-testid={DATA_TEST_IDS.PAYROLL_CREATE_BUTTON}
            size="small"
            onClick={() => setUpdateMode("create")}
          >
            Create a payroll manually
          </Button>
        </Box>
      </Box>
      <ScrollArea
        css={{
          background: vars.colors["surface-neutral-base"],
          display: "flex",
          flexDirection: "column",
          border: "1px solid $outline-neutral-subdue",
          maxHeight: "calc(100vh - 301px)",
          minHeight: 450,
        }}
        data-testid={DATA_TEST_IDS.PAYROLL_SCREEN_BODY}
      >
        <ScrollArea.Viewport css={{ padding: "$3" }}>{renderBody()}</ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation="vertical">
          <ScrollArea.Thumb variant="shadowed" />
        </ScrollArea.Scrollbar>
      </ScrollArea>
    </Box>
  );
};

const MiniPayrollsTable = () => {
  const { setActivePayroll, activePayroll, pagedPayrolls, setPopoverOpen } = usePayrollPage();
  const formatter = useCompanyDateFormatter({ day: "2-digit", month: "2-digit", year: "2-digit" });

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("checkDate", {
        header: "Posted on",
        cell: ({ getValue }) => formatter.format(parseDate(getValue())),
      }),
      columnHelper.display({
        header: "Amount",
        meta: {
          align: "right",
        },
        cell: ({ row }) => formatMoney({ currency: "USD", amount: calculateTotal(row.original) }),
      }),
    ];
  }, [formatter]);

  const table = useDataTable({
    data: pagedPayrolls,
    columns,
  });

  return (
    <DataTable
      css={{
        cursor: "pointer",
        width: "fit-content",
        border: "1px solid $outline-neutral-subdue",
        'tr[aria-checked="true"] td': {
          background: vars.colors["surface-neutral-highlight"],
        },
        "tbody td": { background: vars.colors["surface-neutral-subdue"] },
      }}
      table={table}
      bordered
      onRowClick={({ original }) => {
        setActivePayroll(original.id);
        setPopoverOpen(false);
      }}
      getRowProps={({ original }) => ({
        "aria-checked": original.id === activePayroll?.id,
      })}
    />
  );
};

const PayrollDetails = () => {
  const { activePayroll } = usePayrollPage();
  const formatter = useCompanyDateFormatter({ day: "numeric", month: "short", year: "numeric" });
  const periodFormatter = useCompanyDateFormatter({
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

  if (!activePayroll) return null;

  return (
    <div>
      <div>
        <Box css={{ display: "flex", marginBottom: S["2"] }}>
          <Block>
            <MiniHeader>Posting date</MiniHeader>
            <BlockText>{formatter.format(parseDate(activePayroll.checkDate))}</BlockText>
          </Block>

          {activePayroll.payPeriod && (
            <Block>
              <MiniHeader>Pay period</MiniHeader>
              <BlockText>
                {periodFormatter.formatRange(
                  parseDate(activePayroll.payPeriod.from),
                  parseDate(activePayroll.payPeriod.to)
                )}
              </BlockText>
            </Block>
          )}

          <Block>
            <MiniHeader>Withdrawal date</MiniHeader>
            <BlockText>{formatter.format(parseDate(activePayroll.withdrawalDate))}</BlockText>
          </Block>

          <Block>
            <MiniHeader>Payroll provider</MiniHeader>
            <BlockText>{activePayroll?.providerName}</BlockText>
          </Block>
        </Box>
      </div>

      <Box css={{ marginBottom: S["3"] }}>
        <TransparencyTable payroll={activePayroll} />
      </Box>

      <SummaryTables>
        <Box css={{ flex: "50%" }} data-testid="payable-summary-table">
          <SummaryHeader>Payable summary</SummaryHeader>
          <SummaryTable summaryItems={activePayroll.journalMapping.summaryItems} />
        </Box>

        <Box css={{ flex: "50%" }} data-testid="related-transactions-table">
          <SummaryHeader>Related transactions</SummaryHeader>
          <RelatedTransactionsTable
            transactions={activePayroll.journalMapping.relatedTransactions}
          />
        </Box>
      </SummaryTables>
    </div>
  );
};
